import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, RefreshCw, Clock, AlertTriangle, ArrowRight } from 'lucide-react';

export default function BriefingPacketGenerator({ session }) {
  const [generating, setGenerating] = useState(false);
  const [briefing, setBriefing] = useState(null);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = session?.id ? { session_id: session.id } : {};
      const response = await base44.functions.invoke('councilBriefingPacket', payload);
      const data = response.data || response;
      setBriefing(data);
    } catch (err) {
      console.error('Briefing generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <Card className="glass-panel border-cyan-500/20">
        <CardContent className="py-6 text-center">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-cyan-300">Generating pre-session briefing packet...</p>
        </CardContent>
      </Card>
    );
  }

  if (!briefing) {
    return (
      <Card className="glass-panel border-cyan-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-cyan-400 flex items-center gap-2">
            <FileText className="w-4 h-4" /> AI Briefing Packet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-3">
            Generate an AI briefing with executive summary, recommended triage order, and time estimates.
          </p>
          <Button
            onClick={handleGenerate}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white h-9 text-sm"
          >
            <FileText className="w-4 h-4 mr-2" /> Generate Briefing
          </Button>
        </CardContent>
      </Card>
    );
  }

  const severityColors = {
    regular: 'bg-slate-100 text-slate-700',
    emergency: 'bg-slate-100 text-slate-700',
    backlog_clearing: 'bg-slate-100 text-slate-700'
  };

  return (
    <Card className="glass-panel border-cyan-500/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-cyan-400 flex items-center gap-2">
            <FileText className="w-4 h-4" /> AI Briefing Packet
          </CardTitle>
          <div className="flex items-center gap-2">
            {briefing.recommended_session_type && (
              <Badge className={severityColors[briefing.recommended_session_type] || severityColors.regular}>
                {briefing.recommended_session_type?.replace('_', ' ')}
              </Badge>
            )}
            {briefing.total_estimated_time_minutes && (
              <Badge className="bg-slate-100 text-slate-700">
                <Clock className="w-3 h-3 mr-1" />
                ~{briefing.total_estimated_time_minutes} min
              </Badge>
            )}
            <Button
              size="sm" variant="ghost"
              onClick={handleGenerate}
              className="h-7 text-xs text-cyan-400 hover:text-cyan-300"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Executive Summary */}
        {briefing.executive_summary && (
          <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-1">Executive Summary</p>
            <p className="text-sm text-slate-700">{briefing.executive_summary}</p>
          </div>
        )}

        {/* Queue Overview */}
        {briefing.queue_overview && (
          <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-1">Queue Overview</p>
            <p className="text-xs text-slate-700">{briefing.queue_overview}</p>
          </div>
        )}

        {/* Strategic Context */}
        {briefing.strategic_context && (
          <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-1">Strategic Context</p>
            <p className="text-xs text-slate-700">{briefing.strategic_context}</p>
          </div>
        )}

        {/* Critical Items */}
        {briefing.critical_items?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Critical Items
            </p>
            <div className="space-y-1">
              {Array.isArray(briefing.critical_items) && briefing.critical_items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 rounded bg-slate-100 border border-slate-200">
                  <AlertTriangle className="w-3 h-3 text-slate-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-xs text-slate-700 font-medium">Issue #{item.issue_id}</span>
                    <p className="text-[10px] text-slate-700">{item.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Triage Order */}
        {briefing.recommended_triage_order?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> Recommended Triage Order
            </p>
            <div className="space-y-1">
              {Array.isArray(briefing.recommended_triage_order) && briefing.recommended_triage_order.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded bg-slate-100 border border-slate-200">
                  <span className="text-xs font-bold text-slate-700 w-5 flex-shrink-0">{idx + 1}.</span>
                  <div className="flex-1">
                    <span className="text-xs text-slate-700">Issue #{item.issue_id}</span>
                    <p className="text-[10px] text-slate-700">{item.priority_reason}</p>
                  </div>
                  <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                    <Clock className="w-2.5 h-2.5 mr-0.5" />
                    {item.estimated_minutes}m
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prior Followups */}
        {briefing.prior_followups?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Prior Session Followups</p>
            <div className="space-y-1">
              {Array.isArray(briefing.prior_followups) && briefing.prior_followups.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded bg-slate-100 border border-slate-200">
                  <span className="text-xs text-slate-700 flex-1">{item.description}</span>
                  {item.status && (
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">{item.status}</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}